import { LayerComponentBase } from '@/stellar-globe-components';
import { V4 } from '@/utils/math';
import { Globe, Layer, path, View } from '@hscmap/stellar-globe';
import { mat4 } from 'gl-matrix';
import { Component, Prop } from "vue-property-decorator";
import { RulerModule, ITic } from '.';
import { CoordFormat } from '@/utils/coord_format';

class Renderer extends path.Renderer {
  readonly N = 10

  constructor(readonly globe: Globe, readonly vm: MyComponent) {
    super(globe.gl)
    vm.$watch(() => vm.module.persistent.color, () => {
      this.genArray()
      this.globe.requestRefresh()
    }, { immediate: true })
  }

  darkenNarrowLine = false
  minWidth = 3
  blendMode = path.BlendMode.NORMAL

  genArray() {
    const w = 0.
    const ni = this.N
    const nj = this.N
    const color: V4 = this.vm.module.persistent.color
    const paths: path.Path[] = []
    for (let i = -ni; i <= ni; ++i) {
      paths.push(new path.Path([
        new path.Point([0, -nj, i], color, w),
        new path.Point([0, +nj, i], color, w),
      ], false))
    }
    for (let j = -nj; j <= nj; ++j) {
      paths.push(new path.Path([
        new path.Point([0, j, -ni], color, w),
        new path.Point([0, j, +ni], color, w),
      ], false))
    }
    this.setPaths(paths)
  }

  tic: ITic | null = null

  protected mMatirx() {
    this.refreshTic()
    const tic = this.tic
    this.vm.$emit('update:tic', tic)
    const m = mat4.create()
    if (tic) {
      const { theta, phi } = this.globe.viewFactory
      mat4.rotateZ(m, m, phi)
      mat4.rotateY(m, m, -theta)
      mat4.translate(m, m, [1, 0, 0])
      mat4.scale(m, m, [tic.size, tic.size, tic.size])
    }
    return m
  }

  private refreshTic() {
    const { fovy, aspectRatio } = this.globe.viewFactory
    const fov = aspectRatio > 1 ? fovy * aspectRatio : fovy
    const minTic = fov / this.N
    for (const tic of tics[this.vm.mode]) {
      if (tic.size >= minTic) {
        this.tic = tic
        return
      }
    }
    this.tic = null
  }
}

class MyLayer extends Layer {
  private pr!: Renderer

  constructor(readonly vm: MyComponent) {
    super()
  }

  onAdd() {
    this.pr = new Renderer(this.globe, this.vm)
  }

  onRemove() {
    this.pr.release()
  }

  render(view: View) {
    const alpha = Layer.baseAlpha(view)
    if (alpha > 0) {
      this.pr.alpha = alpha
      this.pr.render(view)
    } else {
      this.vm.$emit('update:tic', null)
    }
  }
}

const tics: { [mode: string]: ITic[] } = (() => {
  function parseAngleText(text: string): number {
    const m = text.match(/(.*?)(deg|arcmin|arcsec)$/)!
    const unit: 'deg' | 'arcmin' | 'arcsec' = m[2] as any
    const angle = parseFloat(m[1]) * {
      deg: Math.PI / 180,
      arcmin: Math.PI / (180 * 60),
      arcsec: Math.PI / (180 * 3600),
    }[unit]
    return angle
  }
  return {
    [CoordFormat.DECIMAL]:
      ` 2deg 1deg
        0.5deg 0.2deg 0.1deg
        0.05deg 0.02deg 0.01deg
        0.005deg 0.002deg 0.001deg
        0.0005deg 0.0002deg 0.0001deg
      `.split(/\s+/).filter((t) => t.length > 0).reverse().map((name) => ({
        name,
        size: parseAngleText(name),
        mode: CoordFormat.DECIMAL,
      })),
    [CoordFormat.SEXADECIMAL]:
      ` 2deg 1deg
        30arcmin 10arcmin 5arcmin 2arcmin 1arcmin
        30arcsec 10arcsec 5arcsec 2arcsec 1arcsec
        0.1arcsec 0.05arcsec 0.02arcsec 0.001arcsec
      `.split(/\s+/).filter((t) => t.length > 0).reverse().map((name) => ({
        name,
        size: parseAngleText(name),
        mode: CoordFormat.SEXADECIMAL,
      })),
  }
})()

@Component
class MyComponent extends LayerComponentBase<MyLayer> {
  @Prop({ required: true })
  module!: RulerModule

  @Prop({ required: true })
  mode!: CoordFormat

  newLayer() {
    return new MyLayer(this)
  }
}

export default MyComponent
